import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';
import { NetworkNode, NetworkLink } from '../../api';
import { GraphTheme } from '../../pages/Connections';
import './NetworkGraph.css';

interface GraphNode extends NodeObject {
  id: string;
  type: 'musician' | 'band';
  name: string;
  photo_url: string | null;
  instruments?: string[];
  connectionCount: number;
  // Force graph adds these
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink extends LinkObject {
  source: string | GraphNode;
  target: string | GraphNode;
}

interface NetworkGraphProps {
  nodes: NetworkNode[];
  links: NetworkLink[];
  searchQuery: string;
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  selectedNodeId?: string | null;
  theme?: GraphTheme;
}

// Colors
const MUSICIAN_COLOR = '#F5A623'; // Yellow/gold
const BAND_COLOR = '#0A84FF'; // Blue
const LINK_COLOR = 'rgba(255, 255, 255, 0.15)';
const HIGHLIGHT_LINK_COLOR = 'rgba(255, 255, 255, 0.6)';
const DIM_OPACITY = 0.2;

const NetworkGraph: React.FC<NetworkGraphProps> = ({
  nodes,
  links,
  searchQuery,
  onNodeClick,
  onNodeHover,
  selectedNodeId,
  theme = 'normal',
}) => {
  const fgRef = useRef<ForceGraphMethods<GraphNode, GraphLink> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Convert nodes/links to graph format
  const graphData = useMemo(() => {
    const graphNodes: GraphNode[] = nodes.map(node => ({
      ...node,
    }));

    const graphLinks: GraphLink[] = links.map(link => ({
      source: link.source,
      target: link.target,
    }));

    return { nodes: graphNodes, links: graphLinks };
  }, [nodes, links]);

  // Find connected nodes for highlighting
  const getConnectedNodes = useCallback((nodeId: string): Set<string> => {
    const connected = new Set<string>();
    connected.add(nodeId);

    links.forEach(link => {
      if (link.source === nodeId) {
        connected.add(link.target);
      } else if (link.target === nodeId) {
        connected.add(link.source);
      }
    });

    return connected;
  }, [links]);

  // Highlighted nodes (from search or selection)
  const highlightedNodes = useMemo(() => {
    const highlighted = new Set<string>();

    // From search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      nodes.forEach(node => {
        if (node.name.toLowerCase().includes(query)) {
          // Add the node and its connections
          const connected = getConnectedNodes(node.id);
          connected.forEach(id => highlighted.add(id));
        }
      });
    }

    // From selection
    if (selectedNodeId) {
      const connected = getConnectedNodes(selectedNodeId);
      connected.forEach(id => highlighted.add(id));
    }

    // From hover
    if (hoveredNode) {
      const connected = getConnectedNodes(hoveredNode.id);
      connected.forEach(id => highlighted.add(id));
    }

    return highlighted;
  }, [searchQuery, selectedNodeId, hoveredNode, nodes, getConnectedNodes]);

  const shouldDim = searchQuery || selectedNodeId || hoveredNode;

  // Node size based on connections
  const getNodeSize = useCallback((node: GraphNode) => {
    const baseSize = node.type === 'band' ? 12 : 8;
    const connectionBonus = Math.min(node.connectionCount * 1.5, 15);
    return baseSize + connectionBonus;
  }, []);

  // Node color
  const getNodeColor = useCallback((node: GraphNode) => {
    const baseColor = node.type === 'musician' ? MUSICIAN_COLOR : BAND_COLOR;

    if (shouldDim && !highlightedNodes.has(node.id)) {
      // Dim non-highlighted nodes
      return node.type === 'musician'
        ? `rgba(245, 166, 35, ${DIM_OPACITY})`
        : `rgba(10, 132, 255, ${DIM_OPACITY})`;
    }

    return baseColor;
  }, [shouldDim, highlightedNodes]);

  // Helper to get rainbow color based on position
  const getRainbowColor = useCallback((x: number, y: number, time: number) => {
    const hue = (Math.atan2(y, x) * 180 / Math.PI + 180 + time) % 360;
    return `hsl(${hue}, 80%, 55%)`;
  }, []);

  // Animation time for trippy mode
  const [animTime, setAnimTime] = useState(0);
  useEffect(() => {
    if (theme !== 'trippy') return;
    const interval = setInterval(() => {
      setAnimTime(t => (t + 2) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, [theme]);

  // Custom node rendering with subtle 3D accent
  const drawNode = useCallback((node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const size = getNodeSize(node);
    const x = node.x || 0;
    const y = node.y || 0;

    // Determine if node should be dimmed based on theme
    let isDimmed = false;
    if (theme === 'dim') {
      // In dim mode, all nodes are dimmed unless highlighted
      isDimmed = !highlightedNodes.has(node.id);
    } else if (theme === 'normal') {
      // Normal mode: dim when there's a search/selection/hover and node isn't highlighted
      isDimmed = !!shouldDim && !highlightedNodes.has(node.id);
    }
    // trippy mode: never dimmed

    // Get base RGB values based on theme
    const isMusician = node.type === 'musician';
    let baseColor: string;
    let shadowColor: string;
    const alpha = isDimmed ? DIM_OPACITY : 1;

    if (theme === 'trippy') {
      baseColor = getRainbowColor(x, y, animTime);
      // For trippy, create a darker version for shadow
      const hue = (Math.atan2(y, x) * 180 / Math.PI + 180 + animTime) % 360;
      shadowColor = `hsl(${hue}, 70%, 35%)`;
    } else {
      const baseRGB = isMusician ? { r: 245, g: 166, b: 35 } : { r: 10, g: 132, b: 255 };
      baseColor = `rgba(${baseRGB.r}, ${baseRGB.g}, ${baseRGB.b}, ${alpha})`;
      shadowColor = `rgba(${Math.max(baseRGB.r - 60, 0)}, ${Math.max(baseRGB.g - 60, 0)}, ${Math.max(baseRGB.b - 60, 0)}, ${alpha})`;
    }

    // Draw base circle
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fillStyle = baseColor;
    ctx.fill();

    // Draw shadow crescent on bottom-right
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.clip();
    ctx.beginPath();
    ctx.arc(x + size * 0.25, y + size * 0.25, size * 1.1, 0, 2 * Math.PI);
    ctx.fillStyle = shadowColor;
    ctx.fill();
    ctx.restore();

    // Draw label if zoomed in enough
    if (globalScale > 0.7 && !isDimmed) {
      const fontSize = Math.min(12 / globalScale, 4);
      ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isDimmed ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(node.name, x, y + size + 2);

      // Show instruments for musicians
      if (node.type === 'musician' && node.instruments && node.instruments.length > 0 && globalScale > 1.2) {
        const instrumentsText = node.instruments.length > 2
          ? `${node.instruments.slice(0, 2).join(', ')} +${node.instruments.length - 2}`
          : node.instruments.join(', ');
        ctx.font = `${fontSize * 0.8}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = theme === 'trippy' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(245, 166, 35, 0.7)';
        ctx.fillText(instrumentsText, x, y + size + fontSize + 4);
      }
    }
  }, [getNodeSize, shouldDim, highlightedNodes, theme, animTime, getRainbowColor]);

  // Link styling
  const getLinkColor = useCallback((link: GraphLink) => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
    const targetId = typeof link.target === 'string' ? link.target : link.target.id;

    if (theme === 'trippy') {
      return 'rgba(255, 255, 255, 0.3)';
    }

    if (theme === 'dim') {
      // In dim mode, links are dim unless both ends are highlighted
      if (highlightedNodes.has(sourceId) && highlightedNodes.has(targetId)) {
        return HIGHLIGHT_LINK_COLOR;
      }
      return 'rgba(255, 255, 255, 0.03)';
    }

    // Normal mode
    if (shouldDim) {
      if (highlightedNodes.has(sourceId) && highlightedNodes.has(targetId)) {
        return HIGHLIGHT_LINK_COLOR;
      }
      return 'rgba(255, 255, 255, 0.03)';
    }

    return LINK_COLOR;
  }, [shouldDim, highlightedNodes, theme]);

  const getLinkWidth = useCallback((link: GraphLink) => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
    const targetId = typeof link.target === 'string' ? link.target : link.target.id;

    if (theme === 'dim' && highlightedNodes.has(sourceId) && highlightedNodes.has(targetId)) {
      return 1.5;
    }
    if (shouldDim && highlightedNodes.has(sourceId) && highlightedNodes.has(targetId)) {
      return 1.5;
    }
    return 0.5;
  }, [shouldDim, highlightedNodes, theme]);

  // Handle node click
  const handleNodeClick = useCallback((node: GraphNode) => {
    if (onNodeClick) {
      onNodeClick(node);
    }

    // Center on the node
    if (fgRef.current) {
      fgRef.current.centerAt(node.x, node.y, 500);
      fgRef.current.zoom(2, 500);
    }
  }, [onNodeClick]);

  // Handle node hover
  const handleNodeHover = useCallback((node: GraphNode | null) => {
    setHoveredNode(node);
    if (onNodeHover) {
      onNodeHover(node);
    }

    // Change cursor
    if (containerRef.current) {
      containerRef.current.style.cursor = node ? 'pointer' : 'grab';
    }
  }, [onNodeHover]);

  // Center on a node when selected externally
  useEffect(() => {
    if (selectedNodeId && fgRef.current) {
      const node = graphData.nodes.find(n => n.id === selectedNodeId);
      if (node && node.x !== undefined && node.y !== undefined) {
        fgRef.current.centerAt(node.x, node.y, 500);
        fgRef.current.zoom(2, 500);
      }
    }
  }, [selectedNodeId, graphData.nodes]);

  return (
    <div ref={containerRef} className="network-graph-container">
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="transparent"
        nodeCanvasObject={drawNode}
        nodePointerAreaPaint={(node, color, ctx) => {
          const size = getNodeSize(node as GraphNode);
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x || 0, node.y || 0, size + 2, 0, 2 * Math.PI);
          ctx.fill();
        }}
        linkColor={getLinkColor}
        linkWidth={getLinkWidth}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        cooldownTicks={100}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        warmupTicks={50}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        minZoom={0.3}
        maxZoom={5}
      />
    </div>
  );
};

export default NetworkGraph;
